/**
 * Finalize Configuration Modal Component
 * Modal for finalizing configuration and creating a new form
 */
import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Select, { components } from "react-select";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { startYear } from "../../core/constants/common";
import GeneralDisclosureService from "../../core/service/GeneralDisclosureService";

const DEFAULT_FORM_STATE = {
  formName: "",
  formDescription: "",
  reviewRequired: false,
  historicalDataSupported: false,
  historicalDataYears: [],
  fileUploadSupported: false,
  showComment: false,
  questionsRequired: false,
  includeOneTimeQuestions: false,
  selectedGDs: [],
};

function FinalizeConfigurationModal({ t, show, onHide, onConfirm }) {
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_STATE });
  const [yearOptions, setYearOptions] = useState([]);
  const [gdOptions, setGdOptions] = useState([]);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const options = [];
    for (let i = currentYear; i >= startYear; i--) {
      options.push({ value: i, label: `${i}` });
    }
    setYearOptions(options);
    GeneralDisclosureService.getStandardTypes()
      .then((response) => {
        const options = (response.data || [])
          .filter((category) => category.categoryType)
          .map((category) => ({ value: category.categoryType, label: category.categoryType }));
        setGdOptions(options);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!show) {
      setFormData({ ...DEFAULT_FORM_STATE });
    }
  }, [show]);

  const handleClose = () => {
    setFormData({ ...DEFAULT_FORM_STATE });
    if (onHide) onHide();
  };

  const handleConfirm = () => {
    if (!formData.formName.trim()) return;
    if (onConfirm) {
      onConfirm({
        name: formData.formName.trim(),
        description: formData.formDescription.trim(),
        reviewRequired: formData.reviewRequired,
        historicalDataSupported: formData.historicalDataSupported,
        historicalDataYears: formData.historicalDataSupported ? formData.historicalDataYears : [],
        fileUploadSupported: formData.fileUploadSupported,
        showComment: formData.showComment,
        optional: !formData.questionsRequired,
        includeOneTimeQuestions: formData.includeOneTimeQuestions,
        selectedGDs: formData.selectedGDs,
      });
    }
    setFormData({ ...DEFAULT_FORM_STATE });
    if (onHide) onHide();
  };

  const getSelectedYears = () =>
    yearOptions.filter((option) => formData.historicalDataYears.includes(option.value));

  const onSelectYears = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      historicalDataYears: (selectedOptions || []).map((option) => option.value),
    }));
  };

  const toggle = (field) =>
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));

  const onToggleIncludeOneTimeQuestions = () => {
    setFormData((prev) => {
      const includeOneTimeQuestions = !prev.includeOneTimeQuestions;
      return {
        ...prev,
        includeOneTimeQuestions,
        historicalDataSupported: includeOneTimeQuestions ? false : prev.historicalDataSupported,
        historicalDataYears: includeOneTimeQuestions ? [] : prev.historicalDataYears,
      };
    });
  };

  const SELECT_ALL_GD_OPTION = { value: "__ALL__", label: t("SelectAll.label") };

  const onSelectGDs = (selectedOptions) => {
    const selected = selectedOptions || [];
    if (selected.some((option) => option.value === SELECT_ALL_GD_OPTION.value)) {
      const isAllSelected = formData.selectedGDs.length === gdOptions.length;
      const selectedGDs = isAllSelected ? [] : gdOptions.map((option) => option.value);
      setFormData((prev) => ({
        ...prev,
        selectedGDs,
        historicalDataSupported: selectedGDs.length ? false : prev.historicalDataSupported,
        historicalDataYears: selectedGDs.length ? [] : prev.historicalDataYears,
      }));
      return;
    }
    const selectedGDs = selected.map((option) => option.value);
    setFormData((prev) => ({
      ...prev,
      selectedGDs,
      historicalDataSupported: selectedGDs.length ? false : prev.historicalDataSupported,
      historicalDataYears: selectedGDs.length ? [] : prev.historicalDataYears,
    }));
  };

  const GDOption = (props) => {
    const isAllOption = props.data.value === SELECT_ALL_GD_OPTION.value;
    const isChecked = isAllOption
      ? gdOptions.length > 0 && formData.selectedGDs.length === gdOptions.length
      : props.isSelected;
    return (
      <components.Option {...props}>
        <input
          type="checkbox"
          className="form-check-input me-2"
          checked={isChecked}
          onChange={() => {}}
        />
        <label className="form-check-label">{props.label}</label>
      </components.Option>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="finalize-configuration-modal-title"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="finalize-configuration-modal-title">
          {t("FinalizeConfiguration.modalTitle.label")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row m-0">

          <div className="col-12 p-2">
            <label className="form-label mb-1">
              {t("FinalizeConfiguration.formName.label")}
              <span className="text-danger ms-1">*</span>
            </label>
            <div className="form-text text-muted mb-1">
              {t("FinalizeConfiguration.formName.description.label")}
            </div>
            <input
              type="text"
              className="form-control"
              value={formData.formName}
              onChange={(e) => setFormData({ ...formData, formName: e.target.value })}
              placeholder={t("FinalizeConfiguration.formName.placeholder.label")}
              required
            />
          </div>

          <div className="col-12 p-2">
            <label className="form-label mb-1">
              {t("FinalizeConfiguration.formDescription.label")}
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={formData.formDescription}
              onChange={(e) => setFormData({ ...formData, formDescription: e.target.value })}
              placeholder={t("FinalizeConfiguration.formDescription.placeholder.label")}
            />
          </div>

          <div className="col-4 p-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="reviewRequiredCheck"
              checked={formData.reviewRequired}
              onChange={() => toggle("reviewRequired")}
            />
            <label className="form-check-label px-1" htmlFor="reviewRequiredCheck">
              {t("DataApprovalNeeded.label")}
            </label>
          </div>

          <div className="col-4 p-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="fileUploadSupportedCheck"
              checked={formData.fileUploadSupported}
              onChange={() => toggle("fileUploadSupported")}
            />
            <label className="form-check-label px-1" htmlFor="fileUploadSupportedCheck">
              {t("FinalizeConfiguration.fileUploadSupported.label")}
            </label>
          </div>

          <div className="col-4 p-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="showCommentCheck"
              checked={formData.showComment}
              onChange={() => toggle("showComment")}
            />
            <label className="form-check-label px-1" htmlFor="showCommentCheck">
              {t("FinalizeConfiguration.showComment.label")}
            </label>
          </div>

          <div className="col-4 p-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="questionsRequiredCheck"
              checked={formData.questionsRequired}
              onChange={() => toggle("questionsRequired")}
            />
            <label className="form-check-label px-1" htmlFor="questionsRequiredCheck">
              {t("FinalizeConfiguration.questionsRequired.label")}
            </label>
          </div>

          <div className="col-4 p-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="hisDataSupportedCheck"
              checked={formData.historicalDataSupported}
              disabled={formData.includeOneTimeQuestions || formData.selectedGDs.length > 0}
              onChange={() => toggle("historicalDataSupported")}
            />
            <label className="form-check-label px-1" htmlFor="hisDataSupportedCheck">
              {t("SupportHistoricalData.label")}
            </label>
          </div>

          <div className="col-4 p-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="includeOneTimeQuestionsCheck"
              checked={formData.includeOneTimeQuestions}
              onChange={onToggleIncludeOneTimeQuestions}
            />
            <label className="form-check-label px-1" htmlFor="includeOneTimeQuestionsCheck">
              {t("FinalizeConfiguration.includeOneTimeQuestions.label")}
            </label>
          </div>

          <div className="col-12 p-2">
            <label className="mb-1" htmlFor="gdSelector">
              {t("GeneralDisclosures.label")}
            </label>
            <Select
              id="gdSelector"
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={[SELECT_ALL_GD_OPTION, ...gdOptions]}
              value={gdOptions.filter((option) => formData.selectedGDs.includes(option.value))}
              onChange={onSelectGDs}
              components={{ Option: GDOption }}
            />
          </div>

          {formData.historicalDataSupported && (
            <div className="col-6 p-2">
              <label className="mb-1" htmlFor="yearSelector">
                {t("Years.label")}
              </label>
              <Select
                id="yearSelector"
                isMulti
                options={yearOptions}
                value={getSelectedYears()}
                onChange={onSelectYears}
              />
            </div>
          )}

          <div className="col-12 p-2">
            <div className="alert alert-info mb-0" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
              <small>{t("FinalizeConfiguration.infoMessage.label")}</small>
            </div>
          </div>

        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("No.label")}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!formData.formName.trim()}
        >
          {t("Yes.label")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

FinalizeConfigurationModal.propTypes = {
  t: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default withTranslation()(FinalizeConfigurationModal);
